import { journalBoundaryContext, boundaryValue } from './boundary-journal.js';
import { activeStatuses, ecId, handBack, HandBack, insertRoll, itemField, pendingStatusDefinitions, record, requiredInt, upper, type JanusCtx, type JanusReaders, type Row } from './context.js';
import { addDirectCharges, adjustCharges } from './charges/adjust-charges.js';
import { abilityChargeEntry, abilityEntryId } from './charges/charge-entry.js';
import { removeStatusEffects, removeStoredStatus } from './statuses/status-effect-removal-helpers.js';
import { carrierEntries, heldStatusDefinition, layerListEntryUuid, passiveActive } from '../reactions/trigger-dispatch.js';
import { janusCtxTriggerReaders } from '../reactions/janus-ctx-trigger-readers.js';
import { janusTriggersCasterOnHit, janusTriggersTargetOnHit } from '../port/k4-dispatch/janus_triggers_on_hit.js';
import { triggerPostRollPassives } from '../port/k4-dispatch/trigger_post_roll_passives.js';
import { type DispatchEnv, type Ref } from '../port/k4-dispatch/dispatch.js';
import { nativePortValue } from '../port/native-value.js';
import { json_encode, PhpArray } from '../php-compat/index.js';
import type { JanusInput } from './janus.js';
import { browserDependencies } from '../port/browser-dependencies.js';
import { statusItemId } from '../statuses/expiry.js';
import { MIRROR_IMAGE_STATUS_IDS } from '../statuses/mirror-image.js';
import { runTriggerFamily } from './trigger-executor.js';
import { EXTRA_DAMAGE_ROLL, EXTRA_WEAPON_DAMAGE_ROLL, statusPassiveDamageSupported } from '../actions/weapon-attack-extra-damage.js';
import { ATTACK_ROLL_TYPE_IDS } from './rolls/attacks-against-cannot-gain-adv.js';
import { effectAttempt } from './boundary-checkpoint.js';
import { creatureName } from '../statuses/gm-boundary.js';
import { nextRollAgainstRider, nextRollRider, ROLL_TRIGGERS } from './skill-roll.js';
import { isAttackPassiveWindow } from '../compile/status-attack-budget.js';
import { frameCopyOnRead } from './frame-snapshot.js';
import { readStatusRowStack } from '../compile/status-stack-reader.js';

/** K4's dependency seams write the existing native truth shape. Nothing sends SQL
 * or starts another commit: ordered charge/status/log changes belong to this use. */
function nativeRow(value: unknown): Row {
  if (value instanceof PhpArray) value = JSON.parse(String(json_encode(value)));
  const row = record(value);
  if (!row) handBack('k4-dispatch:row-unavailable');
  return row;
}

function environment(ctx: JanusCtx, postRoll = false): DispatchEnv {
  const read = janusCtxTriggerReaders(ctx);
  const live = (value: unknown): Row => {
    const row = nativeRow(value);
    return ctx.members.get(ecId(row)) ?? row;
  };
  const definition = (value: unknown): Row => {
    const row = typeof value === 'string' ? null : nativeRow(value);
    if (row && record(row.item_layer_types)) return row;
    const id = typeof value === 'string' ? upper(value) : abilityEntryId(row);
    const held = ctx.read.definition(id);
    if (!held) handBack(`k4-dispatch:definition-not-held:${id}`);
    return held;
  };
  const restore = (value: unknown): Row => {
    const row = typeof value === 'string' ? { item_id: value } : nativeRow(value);
    return { ...row, item_arr: definition(row.item_arr ?? abilityEntryId(row)) };
  };
  const attack = (value: unknown): Row | null => record(nativeRow(value).attack_ability_arr);
  const ids = (value: unknown): string[] => (Array.isArray(value) ? value : [...(value as PhpArray).values()])
    .map(layerListEntryUuid).filter(Boolean);
  const deps = {
    item_field: (value, key) => key === 'item_id' && itemField(nativeRow(value), 'status_id')
      ? statusItemId(nativeRow(value)) : itemField(nativeRow(value), String(key)),
    layer_value: (value, key) => value == null ? null : ctx.read.value(nativeRow(value), String(key)),
    layer_entries: (value, key) => key === 'passive_ability'
      ? carrierEntries(read, live(value), key).map(restore) : ctx.read.entries(nativeRow(value), String(key)),
    layer_has: (value, key) => value != null && (key === 'item_arr'
      ? record(nativeRow(value).item_arr) !== null : ctx.read.has(nativeRow(value), String(key))),
    ft_entry_restore_item_arr: restore,
    ft_layer_entry_needs_resolve: value => typeof value === 'string' || !record(nativeRow(value).item_layer_types),
    ft_resolve_layer_entry: definition,
    passive_ability_entry_is_active: value => passiveActive(read, nativeRow(value)),
    ft_normalize_layer_entries_to_distinct_uuids: ids,
    ability_entry_id: value => abilityEntryId(nativeRow(value)),
    ability_entry_current_charges: (value, owner) => abilityChargeEntry(ctx, live(owner), abilityEntryId(nativeRow(value))).current,
    janus_attack_ability_context_arr: attack,
    janus_nested_attack_ability_overlaps_passive_add_charges: (used, passive) => {
      const nested = attack(used);
      const grants = ids(ctx.read.entries(nativeRow(passive), 'add_charges_to_ability'));
      return !!nested && ids(ctx.read.entries(nested, 'add_charges_to_ability')).some(id => grants.includes(id));
    },
    creature_statuses: value => activeStatuses(ctx, live(value)),
    creature_payload_statuses: value => activeStatuses(ctx, live(value)).map(status => ({ ...status,
      status_arr: heldStatusDefinition(read, status, live(value)) })),
    ft_status_item_passive_entries: value => {
      // Direct passive statuses have conditional-layer/team folding absent from b1.
      // Refuse only this due family; ordinary statuses do not block an on-hit pass.
      for (const status of activeStatuses(ctx, live(value))) {
        const def = heldStatusDefinition(read, status, live(value));
        if (def && !isAttackPassiveWindow(def) && itemField(def, 'item_type') === 'passive_ability'
            && (postRoll ? ctx.read.has(def, 'triggers_when_caster_makes_roll_type')
              && ctx.read.has(def, 'status_effect_removed_from_target_after_cast') : ctx.read.has(def, 'triggers_on_hit')))
          handBack(`k4-dispatch:${postRoll ? 'post-roll' : 'on-hit'}:direct-status-passive-conditions`);
      }
      return [];
    },
    v_status_id_of: value => read.vStatusId(live(value)),
    has_status_effect: (value, id) => read.statusItemIds(live(value)).includes(upper(id)),
    'StatusEffect::getQuantityInfo': id => ({ base: MIRROR_IMAGE_STATUS_IDS.has(upper(id)) ? 'mirror_image_effect' : '' }),
    adjust_charges: (owner: Ref, _affected, id, delta, _roll, log) =>
      adjustCharges(ctx, live(owner.value), upper(id), requiredInt(delta, 'k4-dispatch:charge-delta'), !!log),
    add_charges_to_ability: (used, owner: Ref, _affected, _roll, variable) =>
      addDirectCharges(ctx, nativeRow(used), live(owner.value), variable),
    process_dynamic_layers: () => handBack('k4-dispatch:on-hit:status-dynamic-layers'),
    remove_stackable_status_effect_item_id_single_connection: (_casterId, _caster, _targetId, target, _targets, _origin, id) => {
      const owner = live(target), itemId = upper(id);
      const status = activeStatuses(ctx, owner).find(row => statusItemId(row) === itemId);
      if (!status) handBack('k4-dispatch:on-hit:mirror-image-row-not-held');
      const statusId = upper(ctx.read.value(status, 'status_id'));
      const added = ctx.statusesAdded.find(row => upper(row.status_id) === statusId
        && upper(row.target) === ecId(owner) && upper(row.status_item_id) === itemId);
      if (!ctx.statusStacks) handBack('k4-dispatch:on-hit:stack-writer-unavailable');
      const pending = ctx.statusStacks.find(row => row.status_id === statusId);
      const before = pending?.before ?? readStatusRowStack(status), current = pending?.status_stack ?? before;
      if (current > 1) {
        if (added) added.status_stack = current - 1;
        else if (pending) pending.status_stack = current - 1;
        else ctx.statusStacks.push({ status_id: statusId, status_item_id: itemId, target: ecId(owner), before, status_stack: current - 1 });
        insertRoll(ctx, { roll_type_1: 'status_stack_decreased', roll_item_id: itemId, by: owner, to: owner });
      } else {
        if (pending) ctx.statusStacks.splice(ctx.statusStacks.indexOf(pending), 1);
        removeStoredStatus(ctx, owner, status, 'single');
      }
    },
    insert_log_message: (_roll, type, id, _used, by, to) =>
      insertRoll(ctx, { roll_type_1: String(type), roll_item_id: upper(id), by: live(by), to: live(to) }),
    remove_status_effects: (used, key, _caster, target: Ref) =>
      removeStatusEffects(ctx, nativeRow(used), String(key), live(target.value)),
  } satisfies Partial<DispatchEnv['deps']>;
  // A newly reached seam must refuse before a commit is constructed.
  return browserDependencies(new Proxy(deps, { get: (target, name) => Reflect.get(target, name)
    ?? (() => handBack(`k4-dispatch:dependency:${String(name)}`)) })) as unknown as DispatchEnv;
}

export function runK4OnHit(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row,
  critical = false, attackRollType = upper(ctx.read.value(ability, 'roll_type')),
  { rollHooks = true, skipTargetTail = false, validateRollHook }:
  { rollHooks?: boolean; skipTargetTail?: boolean; validateRollHook?: () => void } = {}): void {
  if (input.useAbilityVariable1 === 'damage_only') return;
  ctx = journalBoundaryContext(ctx); caster = boundaryValue(ctx, caster); target = boundaryValue(ctx, target);
  // Item-type tails inspect the incoming status state, before a disjoint CMRT hook
  // can add its own item-as-status. Existing direct-status refusals still apply.
  const refs = { caster_arr: caster, target_arr: target, affected_ids: [], item_id_used_arr: ability,
    roll_id: '', use_ability_variable_1: input.useAbilityVariable1 };
  const env = environment(ctx);
  const hasCarriers = (owner: Row): boolean => carrierEntries(janusCtxTriggerReaders(ctx), owner, 'passive_ability').length > 0
    || activeStatuses(ctx, owner).length > 0;
  if (hasCarriers(caster)) nativePortValue(janusTriggersCasterOnHit(refs, env));
  const targetTail = (): void => { nativePortValue(janusTriggersTargetOnHit(refs, env)); };
  if (hasCarriers(target)) {
    if (skipTargetTail) effectAttempt(ctx, `${creatureName(ctx, ecId(target))}: opportunity on-hit tail skipped`, targetTail);
    else targetTail();
  }
  // Extra fragments belong to the damage executor; never cast them a second time here.
  if (rollHooks && !ctx.trigger && ATTACK_ROLL_TYPE_IDS.has(attackRollType)) for (const rollType of [attackRollType, '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0']) {
    runTriggerFamily(ctx, { trigger: 'triggers_when_caster_makes_roll_type', rollType, hit: true, criticalHit: critical,
      restrictToAbilitiesWithLayer: ['triggers_on_hit', 'triggers_on_critical_hit', 'triggers_on_not_a_critical_hit'],
      excludeAbilitiesWithLayer: ['triggers_when_caster_uses_item_type', 'triggers_when_target_uses_item_type'],
      excludeAbilityRollTypes: [EXTRA_DAMAGE_ROLL, EXTRA_WEAPON_DAMAGE_ROLL] }, caster, target, [caster], false,
      undefined, false, (candidate, run) => effectAttempt(ctx,
        `${creatureName(ctx, ecId(caster))}: on-hit ${String(itemField(candidate.ability, 'item_name') ?? 'hook')} skipped`, () => {
          const recorder = ctx.read.killTriggerRecorder?.(ctx);
          const addedBefore = ctx.statusesAdded.length;
          run();
          // A hook may commit only statuses that subsequent attacks/OAs can read.
          // Roll back the whole candidate (including charges), never the paid swing.
          for (const status of ctx.statusesAdded.slice(addedBefore)) {
            const statusRow = status as unknown as Row;
            const definition = pendingStatusDefinitions.get(status) ?? ctx.read.definition(statusItemId(statusRow));
            if (!definition) handBack('attack:status-projection:definition-not-held');
            const types = new Set([attackRollType]);
            const roll = { usedRollType: attackRollType, rollerIsTarget: false };
            const against = nextRollAgainstRider(ctx.read, definition, 'target', types, roll);
            const own = nextRollRider(ctx.read, definition, 'caster', types, roll, 'triggers_on_next_roll_type');
            if (!isAttackPassiveWindow(definition) && (itemField(definition, 'item_type') === 'passive_ability' && !statusPassiveDamageSupported(statusRow, definition, ctx.read)
                || !against && !own && ROLL_TRIGGERS.some(key => ctx.read.has(definition, key))
                || !against && ['roll_type_against_adv', 'roll_type_against_dis'].some(key => ctx.read.entries(definition, key).length)))
              handBack('attack:status-projection:carrier-not-supported');
          }
          validateRollHook?.();
          const failure = recorder?.();
          if (failure) handBack(failure);
        }));
  }
}

export function runK4PostRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): void {
  if (input.useAbilityVariable1 === 'damage_only') return;
  const key = `${ecId(caster)}|${input.abilityId}`;
  const done = ctx.k4PostRollFrames ??= new Set<string>();
  if (done.has(key)) return;
  done.add(key);
  try {
    nativePortValue(triggerPostRollPassives({ value: caster }, { value: [] }, ability, '', ctx.statusConnectedId, environment(ctx, true)));
  } catch (error) { done.delete(key); throw error; }
}

/** Item-type/status tails route ordinary clicks through the ability lane.
 * CMRT and OA tails run in the shared weapon consequence frame. */
export function k4OnHitStepAside(encounter: Row, caster: Row, target: Row, ability: Row, read: JanusReaders): string | null {
  const frame = frameCopyOnRead(encounter, new Map([[ecId(caster), caster], [ecId(target), target]]), { catalogs: 'share' });
  const a = frame.members.get(ecId(caster))!, t = frame.members.get(ecId(target))!;
  const ctx: JanusCtx = { ...frame, originals: new Map(), read,
    roll: { d: () => handBack('k4-dispatch:preflight-draw'), fraction: () => handBack('k4-dispatch:preflight-draw') },
    rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [], statusStacks: [],
    statusConnectedId: null, rollPrivate: 0 };
  try {
    runK4OnHit(ctx, { casterId: ecId(a), targetIds: [ecId(t)], abilityId: upper(itemField(ability, 'item_id')),
      rawDiceMod: 0, rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null }, ability, a, t,
      false, undefined, { rollHooks: false });
    // Mirror Image uses the shared weapon consequence frame, including exact-row
    // removal and its loss hooks. Other item-type tails retain their routing.
    const otherRows = ctx.rows.some(row => row.roll_type_1 !== 'lost_a_mirror_image'
      && !(row.roll_type_1 === 'status_stack_decreased' && MIRROR_IMAGE_STATUS_IDS.has(row.roll_item_id ?? '')));
    return ctx.chargesChanged.length || otherRows || ctx.statusesAdded.length ? 'k4-dispatch:on-hit-tail' : null;
  } catch (error) {
    if (error instanceof HandBack) return error.reason;
    throw error;
  }
}

